// Stage-01 data access for the global stakeholders directory (ba'alei inyan,
// spec section 4) — wired to /api/stakeholders. See memory: rights-analysis-rebuild.
import apiConfig from '../../../config/api';

const BASE = apiConfig.apiBaseUrl || '';

// Send the session cookie on every request; the server resolves the user from it.
const FETCH_OPTS = { credentials: 'include' };

// Stakeholder type/category options (spec 4.2 "סיווג / סוג בעל עניין").
export const STAKEHOLDER_TYPES = [
  'יזם',
  'מזמין עבודה',
  'מנהל פרויקט',
  'אדריכל',
  'יועץ',
  'חברה מנהלת',
  'נציג דיירים',
  'אחר',
];

// Plural section headings for the grouped directory ("יזמים", "אדריכלים"…).
export const STAKEHOLDER_TYPE_PLURAL = {
  'יזם': 'יזמים',
  'מזמין עבודה': 'מזמיני עבודה',
  'מנהל פרויקט': 'מנהלי פרויקט',
  'אדריכל': 'אדריכלים',
  'יועץ': 'יועצים',
  'חברה מנהלת': 'חברות מנהלות',
  'נציג דיירים': 'נציגי דיירים',
  'אחר': 'אחר',
};

// Group stakeholders into sections by type, in the canonical STAKEHOLDER_TYPES
// order. Unknown or missing types fall into the trailing "אחר" bucket.
export function groupStakeholdersByType(items) {
  const byType = new Map();
  for (const s of items || []) {
    const t = STAKEHOLDER_TYPES.includes(s.type) ? s.type : 'אחר';
    if (!byType.has(t)) byType.set(t, []);
    byType.get(t).push(s);
  }
  return STAKEHOLDER_TYPES
    .filter((t) => byType.has(t))
    .map((t) => ({ type: t, label: STAKEHOLDER_TYPE_PLURAL[t] || t, items: byType.get(t) }));
}

function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

export async function listStakeholders() {
  const res = await fetch(`${BASE}/api/stakeholders`, { ...FETCH_OPTS, headers: authHeaders() });
  if (!res.ok) throw new Error(`listStakeholders failed: ${res.status}`);
  const data = await res.json();
  return data.stakeholders || [];
}

export async function getStakeholder(id) {
  const res = await fetch(`${BASE}/api/stakeholders?id=${encodeURIComponent(id)}`, { ...FETCH_OPTS, headers: authHeaders() });
  if (!res.ok) throw new Error(`getStakeholder failed: ${res.status}`);
  const data = await res.json();
  return data.stakeholder || null;
}

export async function createStakeholder(payload) {
  const res = await fetch(`${BASE}/api/stakeholders`, {
    ...FETCH_OPTS, method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `createStakeholder failed: ${res.status}`);
  }
  return res.json();
}

export async function updateStakeholder(id, payload) {
  const res = await fetch(`${BASE}/api/stakeholders?id=${encodeURIComponent(id)}`, {
    ...FETCH_OPTS, method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `updateStakeholder failed: ${res.status}`);
  }
  return res.json();
}

export async function deleteStakeholder(id) {
  const res = await fetch(`${BASE}/api/stakeholders?id=${encodeURIComponent(id)}`, {
    ...FETCH_OPTS, method: 'DELETE', headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`deleteStakeholder failed: ${res.status}`);
  return res.json();
}

export function stakeholderInitials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2);
  return parts[0][0] + parts[1][0];
}
